import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PlugZap, RefreshCw, Unplug, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import BillingSection from "@/components/BillingSection";
import BriefingScheduleSection from "@/components/BriefingScheduleSection";

const COMMON_TZS = [
  "Europe/Berlin",
  "Europe/London",
  "Europe/Paris",
  "Europe/Madrid",
  "Europe/Amsterdam",
  "Europe/Stockholm",
  "UTC",
  "America/New_York",
  "America/Los_Angeles",
];

export default function SettingsPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [tz, setTz] = useState("");
  const [pipeline, setPipeline] = useState("");
  const [target, setTarget] = useState("");
  const [owners, setOwners] = useState([]);
  const [quotas, setQuotas] = useState({}); // owner_id -> string
  const nav = useNavigate();

  const load = async () => {
    setErr("");
    try {
      const [{ data }, { data: ownersData }] = await Promise.all([
        api.get("/settings"),
        api.get("/owners").catch(() => ({ data: { owners: [] } })),
      ]);
      setData(data);
      setTz(data.account?.timezone || "Europe/Berlin");
      setPipeline(data.account?.primary_pipeline_id || "");
      setTarget(
        data.account?.quarterly_revenue_target != null
          ? String(data.account.quarterly_revenue_target)
          : ""
      );
      setOwners(ownersData.owners || []);
      const initQ = {};
      const stored = data.account?.per_owner_quotas || {};
      (ownersData.owners || []).forEach((o) => {
        initQ[o.owner_id] = stored[o.owner_id] != null ? String(stored[o.owner_id]) : "";
      });
      setQuotas(initQ);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resync = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.post("/hubspot/resync");
      setSaved(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
      setTimeout(load, 1200);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect HubSpot? Historical snapshots will be kept.")) return;
    setBusy(true);
    setErr("");
    try {
      await api.post("/hubspot/disconnect");
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const [briefingStatus, setBriefingStatus] = useState("");
  const sendTestBriefing = async () => {
    setBriefingStatus("Sending…");
    try {
      const { data } = await api.post("/briefings/test");
      if (data.status === "sent") {
        setBriefingStatus(`Test briefing sent to ${data.to} — check your inbox.`);
      } else {
        setBriefingStatus(data.message || "Couldn't send the briefing — please contact support.");
      }
    } catch (e) {
      setBriefingStatus(formatApiError(e.response?.data?.detail) || "Couldn't send the briefing — please contact support.");
    }
  };

  const savePrefs = async () => {
    setBusy(true);
    setErr("");
    setSaved(false);
    try {
      const payload = { timezone: tz, primary_pipeline_id: pipeline || undefined };
      if (target === "") {
        // don't send; can't unset in this MVP
      } else {
        const n = Number(target);
        if (!Number.isFinite(n) || n < 0) {
          setErr("Quarterly target must be a non-negative number");
          setBusy(false);
          return;
        }
        payload.quarterly_revenue_target = n;
      }
      // Send only owners whose quota value is a positive number (skip blanks).
      const cleanQuotas = {};
      Object.entries(quotas).forEach(([oid, v]) => {
        if (v !== "" && v != null) {
          const n = Number(v);
          if (Number.isFinite(n) && n >= 0) cleanQuotas[oid] = n;
        }
      });
      payload.per_owner_quotas = cleanQuotas;
      await api.patch("/settings", payload);
      setSaved(true);
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto w-full p-6 lg:p-10 text-sm text-slate-500" data-testid="settings-loading">
        Loading…
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto w-full p-6 lg:p-10 space-y-8" data-testid="settings-page">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Workspace</div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">
          Settings
        </h1>
        <p className="mt-3 text-slate-500 max-w-xl">Manage your HubSpot connection and workspace preferences.</p>
      </header>

      <BillingSection />

      <BriefingScheduleSection />

      {/* Connection card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="settings-connection-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">HubSpot Connection</div>
            <div className="mt-2 flex items-center gap-2">
              {data.connected ? (
                <>
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold px-2 py-0.5" data-testid="settings-status-connected">
                    <CheckCircle2 className="h-3 w-3" /> Connected
                  </span>
                  <span className="text-sm text-slate-500">
                    Portal ID <span className="font-mono text-slate-900" data-testid="settings-portal-id">{data.portal_id}</span>
                  </span>
                </>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold px-2 py-0.5" data-testid="settings-status-disconnected">
                  Not connected
                </span>
              )}
            </div>
            <div className="mt-2 text-sm text-slate-500" data-testid="settings-last-sync">
              Last sync: {data.last_sync_at ? new Date(data.last_sync_at).toLocaleString() : "never"}
            </div>
            {data.latest_run && (
              <div className="text-xs text-slate-400 mt-1">
                Latest run: {data.latest_run.status}{data.latest_run.summary ? ` · ${data.latest_run.summary.deals || 0} deals` : ""}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {data.connected ? (
              <>
                <button
                  onClick={resync}
                  disabled={busy}
                  data-testid="settings-resync-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-60"
                >
                  <RefreshCw className="h-4 w-4" /> Re-sync now
                </button>
                <button
                  onClick={disconnect}
                  disabled={busy}
                  data-testid="settings-disconnect-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-white ring-1 ring-red-200 text-red-600 px-4 py-2 text-sm font-semibold hover:bg-red-50 transition-colors disabled:opacity-60"
                >
                  <Unplug className="h-4 w-4" /> Disconnect
                </button>
              </>
            ) : (
              <button
                onClick={() => nav("/dashboard/connect")}
                data-testid="settings-connect-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors"
              >
                <PlugZap className="h-4 w-4" /> Connect HubSpot
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Preferences card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="settings-preferences-card">
        <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Preferences</div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-900 mb-1">Timezone</label>
            <select
              data-testid="settings-tz-select"
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              className="block w-full rounded-md border-0 py-2 px-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 text-sm bg-white"
            >
              {COMMON_TZS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <div className="text-xs text-slate-500 mt-1">
              Nightly sync currently runs at 02:00 UTC for all accounts. Per-account local time coming soon.
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-900 mb-1">Primary pipeline</label>
            <select
              data-testid="settings-pipeline-select"
              value={pipeline}
              onChange={(e) => setPipeline(e.target.value)}
              disabled={!data.pipelines?.length}
              className="block w-full rounded-md border-0 py-2 px-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 text-sm bg-white disabled:opacity-60"
            >
              {!data.pipelines?.length && <option value="">No pipelines synced yet</option>}
              {data.pipelines?.map((p) => (
                <option key={p.pipeline_id} value={p.pipeline_id}>{p.label}</option>
              ))}
            </select>
            <div className="text-xs text-slate-500 mt-1">All analysis uses only the primary pipeline.</div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-900 mb-1">
              Quarterly revenue target
            </label>
            <input
              data-testid="settings-target-input"
              type="number"
              inputMode="decimal"
              min={0}
              step="1000"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="e.g. 500000"
              className="block w-full md:w-1/2 rounded-md border-0 py-2 px-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 text-sm bg-white number-tabular"
            />
            <div className="text-xs text-slate-500 mt-1">
              Used on the Forecast tab to compute your gap = target − (bookings QTD + weighted forecast).
              Currency = your account&apos;s primary currency (detected from open deals).
            </div>
          </div>
        </div>

        {owners.length > 0 && (
          <div className="mt-8 pt-6 border-t border-slate-100" data-testid="settings-owner-quotas">
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-2">
              Per-rep quotas (optional)
            </div>
            <div className="text-xs text-slate-500 mb-4 max-w-2xl">
              If any rep row is left blank, the account&apos;s quarterly revenue target is split evenly
              across all owners for that rep&apos;s coverage calculation on the Team tab.
            </div>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              {owners.map((o) => {
                const name = [o.first_name, o.last_name].filter(Boolean).join(" ") || o.email || o.owner_id;
                return (
                  <div key={o.owner_id} className="grid grid-cols-12 px-4 py-2 border-b border-slate-100 last:border-b-0 items-center">
                    <div className="col-span-8 text-sm text-slate-900 truncate">
                      {name}
                      <span className="text-xs text-slate-400 ml-2">{o.email}</span>
                    </div>
                    <div className="col-span-4">
                      <input
                        data-testid={`settings-quota-${o.owner_id}`}
                        type="number"
                        min={0}
                        step="1000"
                        value={quotas[o.owner_id] || ""}
                        onChange={(e) => setQuotas((prev) => ({ ...prev, [o.owner_id]: e.target.value }))}
                        placeholder="quota"
                        className="block w-full rounded-md border-0 py-1.5 px-2 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 text-sm bg-white number-tabular"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={savePrefs}
            disabled={busy}
            data-testid="settings-save-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-60"
          >
            Save preferences
          </button>
          {saved && (
            <span className="text-sm text-emerald-700 inline-flex items-center gap-1" data-testid="settings-saved">
              <CheckCircle2 className="h-4 w-4" /> Saved
            </span>
          )}
        </div>
      </div>

      {err && <div className="text-sm text-red-600" data-testid="settings-error">{err}</div>}

      {/* Weekly briefing test */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="settings-briefing-card">
        <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Weekly briefing</div>
        <div className="mt-2 text-sm text-slate-600 max-w-2xl">
          Every Monday at 06:00 in your account timezone, we email you a short AI-written
          Monday briefing generated from your latest metrics (Anthropic Claude Sonnet 4.6, delivered via Resend). Everything sent to the AI is pseudonymized — no
          contact names, emails, or company descriptions leave your account.
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            onClick={sendTestBriefing}
            data-testid="settings-test-briefing-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors"
          >
            Send me a briefing now
          </button>
          {briefingStatus && (
            <span className="text-xs text-slate-600" data-testid="settings-test-briefing-status">{briefingStatus}</span>
          )}
        </div>
      </div>
    </div>
  );
}
